/* import __COLOCATED_TEMPLATE__ from './audience-selector-wrapper.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    workflow: $TSFixMe;
    ruleset: $TSFixMe;
    config: $TSFixMe;
    isDisabled?: boolean;
  };
}

export default class WorkflowAudienceSelectorWrapper extends Component<Signature> {
  @service declare appService: any;
  @service declare attributeService: any;
  @service declare intl: IntlService;

  @tracked selectedSegmentIds: number[] = [];
  @tracked audienceMode: 'everyone' | 'custom' | 'fin_audience' = 'everyone';

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this.initializeAudienceState();
  }

  initializeAudienceState() {
    let { workflow } = this.args;

    if (!workflow) {
      this.selectedSegmentIds = [];
      return;
    }

    let persistedSegmentIds = workflow.get('segmentIds') || [];
    this.selectedSegmentIds = [...persistedSegmentIds];
  }

  get app() {
    return this.appService.app;
  }

  get audienceActionProxy() {
    return {
      audienceMode: this.audienceMode,
      selectedSegmentIds: this.selectedSegmentIds,
      switchToEveryone: this.switchToEveryone.bind(this),
      switchToCustom: this.switchToCustom.bind(this),
      toggleSegment: this.toggleSegment.bind(this),
    };
  }

  get rulesEditorConfig() {
    let editorConfiguration = this.args.config.editorConfiguration || {
      workflowMatchingTargetingAttributeIdentifiers: this.getAttributeIdentifiers(),
      workflowMatchingTargetingAttributes: this.getAttributeGroups(),
      supportsUserTargeting: true,
      supportsConversationChannelTargeting: false,
      targetingAttributePickerLabel: this.intl.t(
        'operator.workflows.visual-builder.workflow-audience.add-audience',
      ),
    };

    return {
      ...this.args.config,
      editorConfiguration,
      isDisabled: this.args.isDisabled,
    };
  }

  getAttributeIdentifiers() {
    let {
      filterableUserAttributesForMessaging: userAttrs,
      filterableCompanyAttributesForMessaging: companyAttrs,
    } = this.attributeService;

    let safeUserAttrs = userAttrs || [];
    let safeCompanyAttrs = companyAttrs || [];
    let validAttributes = this.app.companiesActive
      ? [...safeUserAttrs, ...safeCompanyAttrs]
      : safeUserAttrs;
    let identifiers = validAttributes
      .filter((attr: { identifier: any }) => attr && attr.identifier)
      .map((attr: { identifier: any }) => attr.identifier);
    return [...identifiers, 'role'];
  }

  getAttributeGroups() {
    let {
      filterableUserAttributesForMessaging: userAttrs,
      filterableCompanyAttributesForMessaging: companyAttrs,
    } = this.attributeService;

    let groups = [
      {
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.person-data'),
        attributes: userAttrs || [],
      },
    ];
    if (this.app.companiesActive) {
      groups.push({
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.company-data'),
        attributes: companyAttrs || [],
      });
    }
    return groups;
  }

  @action
  switchToEveryone() {
    this.audienceMode = 'everyone';
    this.selectedSegmentIds = [];

    this.args.ruleset?.set?.('predicateGroup.predicates', []);
    this.updateWorkflowAndPersist();
  }

  @action
  switchToCustom() {
    this.audienceMode = 'custom';
    this.selectedSegmentIds = [];

    if (!this.args.ruleset?.predicateGroup) {
      this.args.ruleset?.set?.('predicateGroup', { predicates: [] });
    } else if (!this.args.ruleset?.predicateGroup?.predicates) {
      this.args.ruleset?.set?.('predicateGroup.predicates', []);
    }

    this.updateWorkflowAndPersist();
  }

  @action
  toggleSegment(segmentId: number) {
    let currentIds = [...this.selectedSegmentIds];
    let index = currentIds.indexOf(segmentId);

    if (index > -1) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(segmentId);
    }

    this.selectedSegmentIds = currentIds;
    // Set to fin_audience mode when segments are selected
    this.audienceMode = currentIds.length > 0 ? 'fin_audience' : 'everyone';
    this.updateWorkflowAndPersist();
  }

  private updateWorkflowAndPersist() {
    // Saving logic will be implemented in follow-up PR
  }

  persistAudienceState() {
    this.updateWorkflowAndPersist();
  }

  @action
  onUpdatePredicates(predicates: any) {
    this.updatePredicates('predicateGroup.predicates', predicates);
  }

  @action
  onUpdateRolePredicates(rolePredicates: any) {
    this.updatePredicates('rolePredicateGroup.predicates', rolePredicates);
  }

  private updatePredicates(path: string, predicates: any) {
    let { workflow, ruleset } = this.args;
    if (!ruleset?.set || !workflow?.set) {
      return;
    }

    ruleset.set(path, predicates);
    workflow.set('predicateGroup', ruleset.predicateGroup);
    workflow.send?.('becomeDirty');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::AudienceSelectorWrapper': typeof WorkflowAudienceSelectorWrapper;
    'workflows/audience-selector-wrapper': typeof WorkflowAudienceSelectorWrapper;
  }
}
